/**
 * SMS Service
 * Handles sending SMS messages via Twilio and managing communication history
 */

import { supabase } from '../lib/supabase'
import type {
  Communication,
  CommunicationThread,
  SendSMSData,
  SendSMSResponse,
  RecipientType,
  MessageListFilters,
  MessageListResult
} from '../types/communication'

/**
 * Send an SMS message
 * This calls a Supabase Edge Function that handles the Twilio API
 */
export async function sendSMS(data: SendSMSData): Promise<SendSMSResponse> {
  try {
    // Get current user for sender info
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return { success: false, error: 'User not authenticated' }
    }

    // Get user profile for sender name
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('full_name')
      .eq('user_id', user.id)
      .single()

    // First, create the communication record with pending status
    const { data: communication, error: insertError } = await supabase
      .from('communications')
      .insert({
        message: data.message,
        type: 'sms',
        direction: 'outbound',
        status: 'pending',
        sender_id: user.id,
        sender_name: profile?.full_name || 'Admin',
        recipient_type: data.recipient_type,
        recipient_id: data.recipient_id,
        recipient_name: data.recipient_name,
        recipient_phone: data.recipient_phone,
        assignment_id: data.assignment_id || null
      })
      .select()
      .single()

    if (insertError) {
      console.error('Error creating communication record:', insertError)
      return { success: false, error: 'Failed to create communication record' }
    }

    // Call the Edge Function to send the SMS via Twilio
    const { data: smsResult, error: smsError } = await supabase.functions.invoke('send-sms', {
      body: {
        communicationId: communication.id,
        to: data.recipient_phone,
        message: data.message
      }
    })

    if (smsError) {
      // Update communication status to failed
      await supabase
        .from('communications')
        .update({ 
          status: 'failed',
          provider_error_message: smsError.message 
        })
        .eq('id', communication.id)

      return { success: false, error: smsError.message }
    }

    // Update communication with provider info
    await supabase
      .from('communications')
      .update({
        status: 'sent',
        provider_message_id: smsResult?.messageSid,
        sent_at: new Date().toISOString()
      })
      .eq('id', communication.id)

    return { 
      success: true, 
      communication_id: communication.id,
      messageId: communication.id 
    }
  } catch (error) {
    console.error('Error sending SMS:', error)
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    }
  }
}

/**
 * Get conversation history for a specific recipient
 */
export async function getConversation(
  recipientType: RecipientType,
  recipientId: string
): Promise<Communication[]> {
  const { data, error } = await supabase
    .from('communications')
    .select(`
      *,
      assignments:assignment_id (
        id,
        title,
        assignment_number
      )
    `)
    .eq('recipient_type', recipientType)
    .eq('recipient_id', recipientId)
    .order('created_at', { ascending: true })

  if (error) {
    console.error('Error fetching conversation:', error)
    return []
  }

  return data || []
}

/**
 * Get all communication threads (grouped conversations)
 */
export async function getCommunicationThreads(): Promise<CommunicationThread[]> {
  // Get the latest message from each unique recipient
  const { data, error } = await supabase
    .from('communications')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Error fetching threads:', error)
    return []
  }

  // Group by recipient
  const threadsMap = new Map<string, CommunicationThread>()
  
  data?.forEach(comm => {
    const key = `${comm.recipient_type}-${comm.recipient_id}`
    
    if (!threadsMap.has(key)) {
      threadsMap.set(key, {
        thread_id: comm.id,
        recipient_type: comm.recipient_type,
        recipient_id: comm.recipient_id,
        recipient_name: comm.recipient_name,
        recipient_phone: comm.recipient_phone,
        last_message: comm,
        last_message_at: comm.created_at,
        message_count: 1,
        unread_count: comm.direction === 'inbound' && !comm.read_at ? 1 : 0,
        has_unread: comm.direction === 'inbound' && !comm.read_at
      })
    } else {
      const thread = threadsMap.get(key)!
      thread.message_count = (thread.message_count || 0) + 1
      if (comm.created_at > (thread.last_message_at || '')) {
        thread.last_message = comm
        thread.last_message_at = comm.created_at
      }
      if (comm.direction === 'inbound' && !comm.read_at) {
        thread.has_unread = true
        thread.unread_count = (thread.unread_count || 0) + 1
      }
    }
  })

  return Array.from(threadsMap.values())
}

/**
 * Get unread message count
 */
export async function getUnreadCount(): Promise<number> {
  const { count, error } = await supabase
    .from('communications')
    .select('*', { count: 'exact', head: true })
    .eq('direction', 'inbound')
    .is('read_at', null)

  if (error) {
    console.error('Error fetching unread count:', error)
    return 0
  }

  return count || 0
}

/**
 * Mark messages as read for a specific recipient
 */
export async function markAsRead(
  recipientType: RecipientType,
  recipientId: string
): Promise<void> {
  const { error } = await supabase
    .from('communications')
    .update({ read_at: new Date().toISOString() })
    .eq('recipient_type', recipientType)
    .eq('recipient_id', recipientId)
    .eq('direction', 'inbound')
    .is('read_at', null)

  if (error) {
    console.error('Error marking messages as read:', error)
  }
}

/**
 * Get communications related to a specific assignment
 */
export async function getAssignmentCommunications(
  assignmentId: string
): Promise<Communication[]> {
  const { data, error } = await supabase
    .from('communications')
    .select('*')
    .eq('assignment_id', assignmentId)
    .order('created_at', { ascending: true })

  if (error) {
    console.error('Error fetching assignment communications:', error)
    return []
  }

  return data || []
}

/**
 * Subscribe to new messages (real-time)
 */
export function subscribeToMessages(
  callback: (message: Communication) => void
) {
  const subscription = supabase
    .channel('communications')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'communications'
      },
      (payload) => {
        callback(payload.new as Communication)
      }
    )
    .subscribe()

  return () => {
    subscription.unsubscribe()
  }
}

/**
 * Subscribe to message status updates
 */
export function subscribeToStatusUpdates(
  callback: (message: Communication) => void
) {
  const subscription = supabase
    .channel('communication-updates')
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'communications'
      },
      (payload) => {
        callback(payload.new as Communication)
      }
    )
    .subscribe()

  return () => {
    subscription.unsubscribe()
  }
}

/**
 * Link or unlink a communication to an assignment
 */
export async function linkCommunicationToAssignment(
  communicationId: string,
  assignmentId: string | null
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('communications')
    .update({
      assignment_id: assignmentId,
      link_dismissed: false // Reset dismissed flag when linking
    })
    .eq('id', communicationId)

  if (error) {
    console.error('Error linking communication to assignment:', error)
    return { success: false, error: error.message }
  }

  return { success: true }
}

/**
 * Dismiss link suggestion for a message (user chose not to link)
 * Also removes any existing assignment link
 */
export async function dismissLinkSuggestion(
  communicationId: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await supabase
    .from('communications')
    .update({
      link_dismissed: true,
      assignment_id: null  // Also clear any existing link
    })
    .eq('id', communicationId)

  if (error) {
    console.error('Error dismissing link suggestion:', error)
    return { success: false, error: error.message }
  }

  return { success: true }
}

/**
 * Get unread SMS count for a specific assignment
 */
export async function getAssignmentUnreadCount(
  assignmentId: string
): Promise<number> {
  const { count, error } = await supabase
    .from('communications')
    .select('*', { count: 'exact', head: true })
    .eq('assignment_id', assignmentId)
    .eq('direction', 'inbound')
    .is('read_at', null)

  if (error) {
    console.error('Error fetching assignment unread count:', error)
    return 0
  }

  return count || 0
}

/**
 * Mark assignment communications as read
 */
export async function markAssignmentAsRead(
  assignmentId: string
): Promise<void> {
  const { error } = await supabase
    .from('communications')
    .update({ read_at: new Date().toISOString() })
    .eq('assignment_id', assignmentId)
    .eq('direction', 'inbound')
    .is('read_at', null)

  if (error) {
    console.error('Error marking assignment messages as read:', error)
  }
}

/**
 * Get all messages with filters and pagination
 * Used for the chronological message list view
 */
export async function getAllMessages(
  filters: MessageListFilters = {},
  page: number = 1,
  limit: number = 50
): Promise<MessageListResult> {
  try {
    let query = supabase
      .from('communications')
      .select(`
        *,
        assignments:assignment_id (
          id,
          title,
          assignment_number
        )
      `, { count: 'exact' })

    // Filter by direction
    if (filters.direction && filters.direction !== 'all') {
      query = query.eq('direction', filters.direction)
    }

    // Filter by recipient type
    if (filters.recipientType && filters.recipientType !== 'all') {
      query = query.eq('recipient_type', filters.recipientType)
    }

    // Filter by specific recipient (customer or subcontractor)
    if (filters.recipientId) {
      query = query.eq('recipient_id', filters.recipientId)
    }

    // Filter by system/manual - only applies to outbound messages
    // System SMS: outbound with sender_id = null (automated)
    // Manual SMS: outbound with sender_id != null (sent by admin)
    if (filters.isSystem === true) {
      query = query.eq('direction', 'outbound').is('sender_id', null)
    } else if (filters.isSystem === false) {
      query = query.eq('direction', 'outbound').not('sender_id', 'is', null)
    }

    // Filter by linked status
    if (filters.isLinked === true) {
      query = query.not('assignment_id', 'is', null)
    } else if (filters.isLinked === false) {
      query = query.is('assignment_id', null)
    }

    // Filter by specific assignment
    if (filters.assignmentId) {
      query = query.eq('assignment_id', filters.assignmentId)
    }

    // Filter by read/unread status
    if (filters.status === 'unread') {
      query = query.is('read_at', null).eq('direction', 'inbound')
    } else if (filters.status === 'failed') {
      query = query.eq('status', 'failed')
    } else if (filters.status === 'read') {
      query = query.not('read_at', 'is', null)
    }

    // Filter by date range
    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom + 'T00:00:00')
    }
    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo + 'T23:59:59')
    }

    // Search in message or recipient name
    if (filters.search && filters.search.trim()) {
      const searchTerm = `%${filters.search.trim()}%`
      query = query.or(`message.ilike.${searchTerm},recipient_name.ilike.${searchTerm},recipient_phone.ilike.${searchTerm}`)
    }

    // Sort by created_at descending (newest first)
    query = query.order('created_at', { ascending: false })

    // Pagination
    const offset = (page - 1) * limit
    query = query.range(offset, offset + limit - 1)

    const { data, count, error } = await query

    if (error) {
      console.error('Error fetching all messages:', error)
      return { messages: [], total: 0 }
    }

    return {
      messages: data || [],
      total: count || 0
    }
  } catch (error) {
    console.error('Error in getAllMessages:', error)
    return { messages: [], total: 0 }
  }
}





